import { cn } from '@neo/lib/utils'
import { Button } from '@neo/primitives/button'
import { Tooltip } from '@neo/primitives/tooltip'
import { useStore } from '@xyflow/react'
import { useTypedReactFlow } from '../../hooks/use-react-flow'
import { useFlowStore } from '../../store'
import {
  hasInvalidLeaderProfile,
  isSwitchable,
  LEADER_PROFILE_V1,
  SYSTEM_ONE_THINKING_INVALID,
  stampLeaderProfile,
} from '../../utils/system-one-thinking'
import { LiquidGlass } from './liquid-glass'

// Unlike the other header buttons this one never fills blue on hover: a grey tint instead.
const calmHover = 'hover:border-input hover:bg-accent hover:text-accent-foreground'
// The glass is drawn inside the button and cut off at its edge.
const clipped = 'relative overflow-hidden'
// A white label with a dark halo reads over the glass; the hover: twins keep the grey hover out.
const onLook = cn(
  'border-[#1b1b20] bg-[#0d0d10] text-white hover:border-[#1b1b20] hover:bg-[#0d0d10] hover:text-white',
  '[text-shadow:0_0_8px_rgba(8,4,24,0.95),0_1px_2px_rgba(0,0,0,0.9)]',
)

export const SystemOneThinkingSwitch = () => {
  const leaderProfile = useFlowStore((state) => state.leaderProfile)
  const canSwitch = useStore((state) => state.nodes.some(isSwitchable))
  const isInvalid = useStore((state) => hasInvalidLeaderProfile(state.nodes))
  const { setNodes } = useTypedReactFlow()

  if (!canSwitch && !isInvalid) return null

  const switchTo = (isOn: boolean) => {
    const next = isOn ? LEADER_PROFILE_V1 : null
    useFlowStore.getState().setLeaderProfile(next)
    setNodes((nodes) => stampLeaderProfile(nodes, next))
    useFlowStore.getState().markUserChange()
  }
  // An invalid saved profile reads as on, so the first click removes it.
  const isOn = isInvalid || leaderProfile !== null
  const toggle = () => {
    if (!useFlowStore.getState().isEditing) {
      // The Edit button decides who may edit and when, so view mode goes through it.
      document
        .querySelector<HTMLButtonElement>('[data-testid="button-edit-flow"]:not(:disabled)')
        ?.click()
      if (!useFlowStore.getState().isEditing) return
    }
    switchTo(!isOn)
  }

  return (
    // The Button's own tooltip props re-create the button when the hint comes or goes.
    <Tooltip content={isInvalid ? SYSTEM_ONE_THINKING_INVALID : ''}>
      <div className="ml-1 flex shrink-0 border-l border-st2 pl-3">
        <Button
          type="button"
          variant="outline"
          size="sm"
          // An invalid profile keeps the plain button: its red edge cannot be seen on the glass.
          className={cn(
            clipped,
            calmHover,
            isOn && !isInvalid && onLook,
            isInvalid &&
              'border-destructive text-destructive hover:border-destructive hover:text-destructive',
          )}
          onClick={toggle}
          aria-pressed={isOn}
          aria-invalid={isInvalid}
          data-testid="switch-system-one-thinking"
        >
          {isOn && !isInvalid && (
            <span
              aria-hidden
              data-testid="system-one-glass"
              className="pointer-events-none absolute inset-0 rounded-[inherit]"
            >
              <LiquidGlass className="block h-full w-full rounded-[inherit]" />
            </span>
          )}
          {/* Positioned, so the label is painted over the glass. */}
          <span className="relative">System One Thinking</span>
        </Button>
      </div>
    </Tooltip>
  )
}
